import React, { useState, useEffect, useMemo } from 'react';
import './PredictLayout.css';

const getApiBaseUrl = () => {
  if (process.env.NODE_ENV === 'production') {
    return process.env.REACT_APP_API_BASE_URL || 'https://knotcord.com';
  }
  const hostname = window.location.hostname;
  if (hostname === 'localhost' || hostname === '127.0.0.1' || hostname.match(/^\d+\.\d+\.\d+\.\d+$/)) {
    return '';
  }
  return process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';
};

const API_BASE_URL = getApiBaseUrl();
const PREDICT_URL = `${API_BASE_URL}/zoulang/predict/latest/`;

const MODEL_ORDER = ['monkey_fist', 'carrick', 'celtic'];

const MODEL_DISPLAY = {
  monkey_fist: 'Monkey Fist',
  carrick: 'Carrick',
  celtic: 'Celtic',
};

const MODEL_DESC = {
  monkey_fist: 'A baseline model to start.',
  carrick: 'Aggressive version of model Monkey Fist. Volatile and overspecializes to extremes.',
  celtic: 'Balanced version of model Carrick.',
};

const formatDate = (dateStr) => {
  const d = new Date(dateStr + 'T00:00:00');
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const Calendar = ({ dates, selectedDate, onDateClick }) => {
  const dateSet = useMemo(() => new Set(dates), [dates]);
  const init = new Date((selectedDate || dates[0]) + 'T00:00:00');
  const [viewYear, setViewYear] = useState(init.getFullYear());
  const [viewMonth, setViewMonth] = useState(init.getMonth());

  const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();
  const startDay = new Date(viewYear, viewMonth, 1).getDay();
  const label = new Date(viewYear, viewMonth).toLocaleDateString('en-US', { month: 'short', year: 'numeric' });

  const nav = (delta) => {
    const dt = new Date(viewYear, viewMonth + delta);
    setViewYear(dt.getFullYear());
    setViewMonth(dt.getMonth());
  };

  return (
    <div className="predict-calendar">
      <div className="predict-cal-header">
        <button className="predict-cal-nav" onClick={() => nav(-1)}>&#8249;</button>
        <span className="predict-cal-month">{label}</span>
        <button className="predict-cal-nav" onClick={() => nav(1)}>&#8250;</button>
      </div>
      <div className="predict-cal-weekdays">
        {['S','M','T','W','T','F','S'].map((d, i) => <div key={i}>{d}</div>)}
      </div>
      <div className="predict-cal-grid">
        {Array.from({ length: startDay }, (_, i) => <div key={`e${i}`} />)}
        {Array.from({ length: daysInMonth }, (_, i) => {
          const day = i + 1;
          const ds = `${viewYear}-${String(viewMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
          const avail = dateSet.has(ds);
          return (
            <button
              key={day}
              className={`predict-cal-day${avail ? ' available' : ''}${ds === selectedDate ? ' selected' : ''}`}
              disabled={!avail}
              onClick={() => onDateClick(ds)}
            >
              {day}
            </button>
          );
        })}
      </div>
    </div>
  );
};

const RankTable = ({ header, rows }) => (
  <div className="predict-model-card">
    <div className="predict-model-header">{header}</div>
    <div className="predict-table-scroll">
      <table className="predict-table">
        <thead>
          <tr>
            <th className="predict-th-rank">#</th>
            <th className="predict-th-symbol">Symbol</th>
          </tr>
        </thead>
        <tbody>
          {rows && rows.map((r, i) => (
            <tr key={i} className="predict-row">
              <td className="predict-td-rank">{r.rank}</td>
              <td className="predict-td-symbol">{r.symbol}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);

const PredictLayout = () => {
  const [data, setData] = useState(null);
  const [dates, setDates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [viewMode, setViewMode] = useState('date');
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedModel, setSelectedModel] = useState(null);
  const [dateOffset, setDateOffset] = useState(0);
  const [confidenceMap, setConfidenceMap] = useState({});

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await fetch(PREDICT_URL, {
          method: 'GET',
          credentials: 'include',
        });
        if (!response.ok) {
          if (response.status === 401) {
            throw new Error('Authentication required. Please log in.');
          }
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.error || 'Failed to load predictions. Please try again later.');
        }
        const json = await response.json();
        setDates(json.dates || []);
        setData(json.predictions || {});
        if (json.dates && json.dates.length > 0) {
          setSelectedDate(json.dates[0]);
        }
        if (json.market_confidence) {
          setConfidenceMap(json.market_confidence);
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const handleDateClick = (d) => {
    setViewMode('date');
    setSelectedDate(d);
    setSelectedModel(null);
  };

  const handleModelClick = (m) => {
    setViewMode('model');
    setSelectedModel(m);
    setSelectedDate(null);
    setDateOffset(0);
  };

  if (loading) {
    return (
      <div className="predict-page">
        <div className="predict-frame">
          <div className="predict-topbar">
            <button className="predict-topbar-back" onClick={() => window.history.back()}>&#8592; back</button>
          </div>
          <div className="predict-body">
            <div className="predict-status">loading predictions...</div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="predict-page">
        <div className="predict-frame">
          <div className="predict-topbar">
            <button className="predict-topbar-back" onClick={() => window.history.back()}>&#8592; back</button>
          </div>
          <div className="predict-body">
            <div className="predict-status predict-status-error">{error}</div>
          </div>
        </div>
      </div>
    );
  }

  if (!dates.length) {
    return (
      <div className="predict-page">
        <div className="predict-frame">
          <div className="predict-topbar">
            <button className="predict-topbar-back" onClick={() => window.history.back()}>&#8592; back</button>
          </div>
          <div className="predict-body">
            <div className="predict-status">no prediction data available.</div>
          </div>
        </div>
      </div>
    );
  }

  let cards = [];
  const canShiftLeft = viewMode === 'model' && dateOffset > 0;
  const canShiftRight = viewMode === 'model' && dateOffset + 3 < dates.length;
  if (viewMode === 'date' && selectedDate && data[selectedDate]) {
    const dayData = data[selectedDate];
    cards = MODEL_ORDER.map((model) => ({
      key: model,
      header: MODEL_DISPLAY[model] || model,
      rows: dayData[model] || [],
    }));
  } else if (viewMode === 'model' && selectedModel) {
    const windowDates = dates.slice(dateOffset, dateOffset + 3);
    cards = windowDates.map((d) => ({
      key: d,
      header: formatDate(d),
      rows: (data[d] && data[d][selectedModel]) || [],
    }));
  }

  return (
    <div className="predict-page">
      <div className="predict-frame">
        <div className="predict-topbar">
          <button className="predict-topbar-back" onClick={() => window.history.back()}>&#8592; back</button>
          <span className="predict-topbar-title">predictions</span>
          <span className="predict-topbar-spacer" />
          <span className="predict-topbar-label">
            {viewMode === 'date'
              ? `${formatDate(selectedDate)} — all models`
              : MODEL_DISPLAY[selectedModel]}
          </span>
        </div>

        <div className="predict-body">
          <div className="predict-sidebar">
            <Calendar dates={dates} selectedDate={selectedDate} onDateClick={handleDateClick} />

            <div className="predict-sidebar-block">
              <div className="predict-sidebar-block-title">next session</div>
              <div className="predict-sidebar-block-desc">
                model consensus for the next trading day. 1 &mdash; cut or sit out &middot; 4 &mdash; size up.
              </div>
              {selectedDate && confidenceMap[selectedDate] != null ? (
                <div className="predict-conf-score">{confidenceMap[selectedDate]}</div>
              ) : (
                <div className="predict-conf-na">&mdash;</div>
              )}
            </div>

            <div className="predict-sidebar-block predict-model-nav">
              <div className="predict-sidebar-block-title">models</div>
              {MODEL_ORDER.map((m) => (
                <div key={m} className="predict-model-item">
                  <button
                    className={`predict-model-btn${viewMode === 'model' && m === selectedModel ? ' active' : ''}`}
                    onClick={() => handleModelClick(m)}
                  >
                    {MODEL_DISPLAY[m]}
                  </button>
                  <div className="predict-model-desc">{MODEL_DESC[m]}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="predict-divider-v" />

          <div className="predict-content">
            <div className="predict-grid-wrapper">
              {viewMode === 'model' && (
                <button
                  className="predict-arrow predict-arrow-left"
                  disabled={!canShiftLeft}
                  onClick={() => setDateOffset(dateOffset - 1)}
                >&#8249;</button>
              )}
              <div className="predict-grid">
                {cards.map((c) => (
                  <RankTable key={c.key} header={c.header} rows={c.rows} />
                ))}
              </div>
              {viewMode === 'model' && (
                <button
                  className="predict-arrow predict-arrow-right"
                  disabled={!canShiftRight}
                  onClick={() => setDateOffset(dateOffset + 1)}
                >&#8250;</button>
              )}
            </div>
            <p className="predict-disclaimer">
              beta version. markets are volatile. predictions are for research purposes only. not investment advice.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PredictLayout;
